/**
 * FEATURE FLAGS — NetUno
 *
 * Controle centralizado de visibilidade de módulos.
 * Para reativar um módulo, altere o valor correspondente para `true`.
 *
 * ─────────────────────────────────────────────────────────────────
 * MÓDULO: GESTOR_PERFORMANCE
 * Status: OCULTO (false)
 * Ocultado em: 2026-02-19
 * Motivo: Módulo em avaliação para refatoração/substituição.
 *         Conteúdo preservado integralmente para análise futura e
 *         possível criação do módulo "SMS de Campo", derivado do
 *         conjunto de métricas e dashboards de desempenho de gestores.
 * Reativação: Alterar `GESTOR_PERFORMANCE_ENABLED` para `true`.
 * ─────────────────────────────────────────────────────────────────
 */
export const FEATURE_FLAGS = {
  /**
   * Módulo "Desempenho Gestores" (/empreendimentos/gestor-performance)
   * Ocultado temporariamente. Dados e lógica 100% preservados.
   * Derivado futuro: módulo "SMS de Campo"
   */
  GESTOR_PERFORMANCE_ENABLED: false,

  /**
   * SLICE 1 — Criação mínima de OS (1 campo: descrição).
   * Quando ativo, "Nova OS" abre o CriarOSMinimoDialog.
   * Quando inativo, mantém o dialog legacy completo.
   * Toggle local: localStorage.setItem('ff:slice1_os_minima', '1')
   */
  SLICE1_OS_MINIMA:
    (typeof window !== 'undefined' &&
      window.localStorage?.getItem('ff:slice1_os_minima') === '1') ||
    (typeof import.meta !== 'undefined' &&
      (import.meta as any).env?.VITE_FF_SLICE1_OS_MINIMA === 'true'),

  /**
   * SLICE 2 — Banner guiado para 1º item da OS (PPU ou EAP).
   * Substitui o "Nenhum item nesta OS." por CTA dual.
   * Independente do Slice 1.
   * Toggle local: localStorage.setItem('ff:slice2_primeiro_item_guiado', '1')
   */
  SLICE2_PRIMEIRO_ITEM_GUIADO:
    (typeof window !== 'undefined' &&
      window.localStorage?.getItem('ff:slice2_primeiro_item_guiado') === '1') ||
    (typeof import.meta !== 'undefined' &&
      (import.meta as any).env?.VITE_FF_SLICE2_PRIMEIRO_ITEM_GUIADO === 'true'),

  /**
   * SLICE 3 — Auto-start inteligente: ao abrir uma OS recém criada (rascunho,
   * 0 itens, criada há <10s ou via flag de navegação), o banner guiado já
   * entra direto no modo PPU com seletor aberto e foco no campo de busca.
   * Independente dos slices anteriores. Requer Slice 2 ativo para ter efeito visual.
   * Toggle local: localStorage.setItem('ff:slice3_auto_start_os', '1')
   */
  SLICE3_AUTO_START_OS:
    (typeof window !== 'undefined' &&
      window.localStorage?.getItem('ff:slice3_auto_start_os') === '1') ||
    (typeof import.meta !== 'undefined' &&
      (import.meta as any).env?.VITE_FF_SLICE3_AUTO_START_OS === 'true'),

  /**
   * SLICE 4 — Continuidade inteligente (next-best action) após o 1º item.
   * Mostra painel "Próximo passo sugerido" com 3 ações: similar, próximo, enviar.
   * Independente dos demais slices.
   * Toggle local: localStorage.setItem('ff:slice4_next_best_action', '1')
   */
  SLICE4_NEXT_BEST_ACTION:
    (typeof window !== 'undefined' &&
      window.localStorage?.getItem('ff:slice4_next_best_action') === '1') ||
    (typeof import.meta !== 'undefined' &&
      (import.meta as any).env?.VITE_FF_SLICE4_NEXT_BEST_ACTION === 'true'),
} as const;

// ─────────────────────────────────────────────────────────────────
// SLICE F3 — Boletim mínimo (Safety Layer v2 / sentinela)
// Ativação automática: ambiente dev OU cluster ∈ PILOT_CLUSTERS_F3.
// Não depende de localStorage manual nem ativação global.
// Rollback: sair do ambiente / cluster piloto.
// ─────────────────────────────────────────────────────────────────

/** Clusters piloto autorizados a usar o fluxo mínimo de boletim (F3).
 *  Mantido em sincronia com o backend (emp_feature_flags.modulo='slice_boletim_minimo').
 *  Ambiente atual = 100% dados de teste, todos os clusters entram no piloto.
 */
export const PILOT_CLUSTERS_F3: readonly string[] = [
  '08f7d0f3-f4da-4d30-bbb4-67f8e7a90f75', // 3LS
  'c7c070ce-70b2-43be-b427-6bae55fc41a3', // Cluster EPC S/A
  '7b7464d0-e1cb-44e0-b9b2-3eaf25e9d333', // Cluster Teste Validação
  '35e6bfb4-7afb-4c2b-97ab-f29a8bd9312d', // Construtora ABC
  '2ff49d16-3db3-4691-8983-a4327af5a32f', // Empresa Carioca de Obras
];

/** True quando rodando em ambiente de desenvolvimento (Vite DEV ou hostname local/preview). */
export function isDevEnvironment(): boolean {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV === true) return true;
  } catch {
    /* noop */
  }
  if (typeof window !== 'undefined') {
    const h = window.location?.hostname ?? '';
    if (h === 'localhost' || h === '127.0.0.1') return true;
    if (h.endsWith('.lovable.app') || h.endsWith('.lovableproject.com')) return true;
  }
  return false;
}

/**
 * Determina se o slice F3 (Boletim mínimo) está auto-habilitado.
 * Regra: dev OU cluster piloto. Sem flag manual / localStorage.
 */
export function isSliceF3AutoEnabled(clusterId?: string | null): boolean {
  const enabled =
    isDevEnvironment() || (!!clusterId && PILOT_CLUSTERS_F3.includes(clusterId));
  // Observabilidade mínima exigida pelo escopo do slice.
  // eslint-disable-next-line no-console
  console.log('[SLICE_F3_AUTO] enabled:', enabled, { clusterId: clusterId ?? null });
  return enabled;
}

// ─────────────────────────────────────────────────────────────────
// MODULE TIERS — Classificação comercial de funcionalidades
// ─────────────────────────────────────────────────────────────────

export type ModuleTier = 'basico' | 'profissional' | 'enterprise';
export type ModuleType = 'tier' | 'addon';

export interface ModuleDefinition {
  /** Identificador interno (rota ou grupo) */
  id: string;
  /** Tier mínimo necessário para acesso (para módulos tipo 'tier') */
  tier: ModuleTier;
  /** Rótulo para relatório de acessos */
  label: string;
  /** Tipo do módulo: 'tier' (padrão) ou 'addon' (avulso) */
  type: ModuleType;
}

// ─────────────────────────────────────────────────────────────────
// ADDON DEFINITIONS — Módulos avulsos independentes de tier
// ─────────────────────────────────────────────────────────────────

export interface AddonDefinition {
  /** ID do addon (corresponde ao campo 'modulo' em emp_feature_flags) */
  id: string;
  /** Nome comercial */
  name: string;
  /** Descrição curta */
  description: string;
  /** Ícone (referência para UI) */
  icon: string;
  /** Roles que podem visualizar o addon quando ativo */
  roles: string[];
}

export const ADDON_DEFINITIONS: AddonDefinition[] = [
  {
    id: 'addon_erp',
    name: 'NetUno Connect',
    description: 'Integração ERP (TOTVS, SAP) com reconciliação automática',
    icon: 'Plug',
    roles: ['admin', 'coadmin'],
  },
  {
    id: 'addon_bim',
    name: 'NetUno Vision',
    description: 'Visualização 3D e rastreabilidade BIM/IFC',
    icon: 'Box',
    roles: ['admin', 'coadmin', 'admin_cluster'],
  },
];

/**
 * Mapa canônico de módulos por tier.
 * 
 * Básico: Autenticação, Dashboard, Mapa
 * Profissional: Operação completa, Contratos, Planejamento, Arquivo & Databooks, Qualidade
 * Enterprise: Portfólio, Financeiro, Auditoria, Gates, Governança
 * 
 * NOTA: Integração ERP e BIM foram extraídos para addons independentes.
 */
export const MODULE_TIERS: ModuleDefinition[] = [
  // ── Básico ──
  { id: 'dashboard',       tier: 'basico',        label: 'Dashboard',              type: 'tier' },
  { id: 'mapa',            tier: 'basico',        label: 'Mapa',                   type: 'tier' },
  { id: 'manual',          tier: 'basico',        label: 'Manual',                 type: 'tier' },

  // ── Profissional ──
  { id: 'contratos',       tier: 'profissional',  label: 'Contratos',              type: 'tier' },
  { id: 'planejamento',    tier: 'profissional',  label: 'Planejamento',           type: 'tier' },
  { id: 'arquivo',         tier: 'profissional',  label: 'Arquivo & Databooks',    type: 'tier' },
  { id: 'campo',           tier: 'profissional',  label: 'Campo (Execução)',       type: 'tier' },
  { id: 'qualidade',       tier: 'profissional',  label: 'Qualidade',              type: 'tier' },
  { id: 'clusters',        tier: 'profissional',  label: 'Clusters',               type: 'tier' },

  // ── Enterprise ──
  { id: 'portfolio',       tier: 'enterprise',    label: 'Dashboard Executivo',    type: 'tier' },
  { id: 'execucao-global', tier: 'enterprise',    label: 'Execução (Portfólio)',   type: 'tier' },
  { id: 'medicoes-global', tier: 'enterprise',    label: 'Medições (Portfólio)',   type: 'tier' },
  { id: 'indicadores',     tier: 'enterprise',    label: 'Indicadores',            type: 'tier' },
  { id: 'risk-analytics',  tier: 'enterprise',    label: 'Analytics de Risco',     type: 'tier' },
  { id: 'alertas',         tier: 'enterprise',    label: 'Alertas',                type: 'tier' },
  { id: 'relatorios',      tier: 'enterprise',    label: 'Relatórios',             type: 'tier' },
  { id: 'financeiro',      tier: 'enterprise',    label: 'Financeiro (contrato)',  type: 'tier' },
  { id: 'auditoria',       tier: 'enterprise',    label: 'Auditoria',              type: 'tier' },
  { id: 'quality-gate',    tier: 'enterprise',    label: 'Quality Gate',           type: 'tier' },
  { id: 'planning-gate',   tier: 'enterprise',    label: 'Planning Gate',          type: 'tier' },
  { id: 'sla-uptime',      tier: 'enterprise',    label: 'SLA & Uptime',           type: 'tier' },
  { id: 'governanca',      tier: 'enterprise',    label: 'Governança',             type: 'tier' },
  { id: 'sandbox',         tier: 'enterprise',    label: 'Sandbox de Testes',      type: 'tier' },

  // ── Addons (tier irrelevante — gating independente) ──
  { id: 'addon_erp',       tier: 'basico',        label: 'NetUno Connect (ERP)',   type: 'addon' },
  { id: 'addon_bim',       tier: 'basico',        label: 'NetUno Vision (BIM)',    type: 'addon' },
];

/**
 * Verifica se um módulo está acessível para um dado tier de tenant.
 * Hierarquia: basico < profissional < enterprise
 * 
 * NOTA: Módulos do tipo 'addon' sempre retornam true aqui.
 * O gating real de addons é feito via hasAddon() no useTenantTier.
 */
export function isModuleAccessible(moduleId: string, tenantTier: ModuleTier): boolean {
  const tierRank: Record<ModuleTier, number> = { basico: 1, profissional: 2, enterprise: 3 };
  const mod = MODULE_TIERS.find(m => m.id === moduleId);
  if (!mod) return true; // módulos não mapeados são acessíveis
  if (mod.type === 'addon') return true; // addons são gateados separadamente
  return tierRank[tenantTier] >= tierRank[mod.tier];
}

/**
 * Gera relatório de módulos por tier para exibição administrativa.
 */
export function getModulesByTier(): Record<ModuleTier, ModuleDefinition[]> {
  return {
    basico: MODULE_TIERS.filter(m => m.tier === 'basico' && m.type === 'tier'),
    profissional: MODULE_TIERS.filter(m => m.tier === 'profissional' && m.type === 'tier'),
    enterprise: MODULE_TIERS.filter(m => m.tier === 'enterprise' && m.type === 'tier'),
  };
}

/**
 * Retorna definições de addons disponíveis.
 */
export function getAddonDefinitions(): AddonDefinition[] {
  return ADDON_DEFINITIONS;
}
